/**
 * Where a transport page should send the user when they finish (back, cancel,
 * save). Two sources, carried in the URL so they survive reloads and the
 * detail → edit hop:
 *
 * - `returnToOrderId` — the user came from an order's transport tab.
 * - `returnTo` — the user came from an in-app screen with its own state
 *   (typically the transport list with a status view / search / sort), and
 *   should land on exactly that screen again instead of the default list.
 *
 * `returnTo` is restricted to in-app `/app/...` paths so a crafted link can
 * never turn it into an open redirect.
 */
export interface TransportReturnContext {
  orderId?: string;
  returnTo?: string;
}

export const RETURN_TO_ORDER_PARAM = 'returnToOrderId';
export const RETURN_TO_PARAM = 'returnTo';

export function isSafeReturnPath(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.startsWith('/app/') && !value.startsWith('//');
}

export function readTransportReturnContext(search: URLSearchParams): TransportReturnContext | null {
  const orderId = search.get(RETURN_TO_ORDER_PARAM) || undefined;
  const returnToRaw = search.get(RETURN_TO_PARAM);
  const returnTo = isSafeReturnPath(returnToRaw) ? returnToRaw : undefined;
  if (!orderId && !returnTo) return null;
  return { ...(orderId ? { orderId } : {}), ...(returnTo ? { returnTo } : {}) };
}

export function buildOrderTransportPath(orderId: string): string {
  return `/app/orders/${encodeURIComponent(orderId)}?tab=transport`;
}

/** The screen to go back to; order tab wins, then an explicit `returnTo`, then the caller's fallback. */
export function resolveTransportReturnPath(
  context: TransportReturnContext | null | undefined,
  fallback: string,
): string {
  if (context?.orderId) return buildOrderTransportPath(context.orderId);
  if (context?.returnTo) return context.returnTo;
  return fallback;
}

/** Appends the return context to an in-app path so the next transport page keeps it. */
export function withTransportReturnContext(
  path: string,
  context: TransportReturnContext | string | null | undefined,
): string {
  const normalized: TransportReturnContext | null = typeof context === 'string' ? { orderId: context } : context ?? null;
  if (!normalized?.orderId && !normalized?.returnTo) return path;
  const parts: string[] = [];
  if (normalized.orderId) parts.push(`${RETURN_TO_ORDER_PARAM}=${encodeURIComponent(normalized.orderId)}`);
  if (normalized.returnTo) parts.push(`${RETURN_TO_PARAM}=${encodeURIComponent(normalized.returnTo)}`);
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${parts.join('&')}`;
}

/** Builds a `returnTo` context pointing at the current screen (pathname + search). */
export function returnToCurrentScreen(location: { pathname: string; search: string }): TransportReturnContext {
  return { returnTo: `${location.pathname}${location.search}` };
}
